import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { BoundingBox } from "../../core/mdl/BoundingBox";
import { GltfMesh } from "../../core/mdl/io/GltfMesh";
import { NodeUserData } from "../../core/mdl/NodeUserData";
import { PbrMaterial } from "../../core/mdl/PbrMaterial";
import { Texture } from "../../core/mdl/Texture";
import { Log } from "../../utils";
import { ajax, Lock } from "../../utils/async";
import { clamp, degToRad } from "../../utils/math";
import { isNullOrEmpty as objectIsNullOrEmpty } from "../../utils/object";
import { getFileName, hash, isNotNullOrEmpty, isNullOrEmpty, isString } from "../../utils/string";
import { ARTICLEELEMENT_NODEDATA_BASKETID, ARTICLEELEMENT_NODEDATA_SELECTABLE } from "../Constants";
export class GfjLoader {
    constructor(pModelManager) {
        this.onResolveUrl = null;
        this.mScene = pModelManager.scene;
        this.mLayerManager = pModelManager.layers;
        this.mGeometryManager = pModelManager.geometries;
        this.mMaterialManager = pModelManager.materials;
        this.mPreloadResourcesLock = new Lock();
        this.mResourceLoadingLock = new Lock(5); //load max 5 parallel
        this.mLoadingTexturesMap = new Map();
    }
    /**
     * Load gfj from url. Returns an array of loaded nodes.
     * @param pUrl The gfj url.
     * @param pBasketItems List of basket items (main article + sub articles) which belong to this gfj. (gfj should contain only one root object)
     * @param pContainer Optional container. If not null the gfj url should be a path to a file inside the container.
     */
    load(pUrl, pRootBasketId, pBasketItems, pContainer) {
        if (isNullOrEmpty(pUrl))
            return (Promise.resolve(null));
        let tGfjPromise = null;
        if (pContainer == null)
            tGfjPromise = ajax("GET", pUrl);
        else
            tGfjPromise = pContainer.getFile(pUrl, false);
        const tPromise = tGfjPromise.then((pResponse) => {
            return (this.loadData(JSON.parse(pResponse), pRootBasketId, pBasketItems, pContainer));
        });
        return (tPromise);
    }
    async loadData(pGfjData, pRootBasketId, pBasketItems, pContainer) {
        //abort if gfj does not contain geometries
        if (objectIsNullOrEmpty(pGfjData.Geometries))
            return (null);
        //pre-load resources
        await this.preLoadResources(pGfjData, pContainer);
        //build map from geometry id to basket id
        let tBasketIdMap = null;
        if (pBasketItems != null) {
            tBasketIdMap = new Map();
            for (const tItem of pBasketItems) {
                if (tItem.geometryId != null) {
                    //check if there are cases of duplicate geometry ids, in this case use an array of basket ids
                    const tCurrentBasketId = tBasketIdMap.get(tItem.geometryId);
                    if (tCurrentBasketId == null) {
                        tBasketIdMap.set(tItem.geometryId, tItem.itemId);
                    }
                    else {
                        //use an array and add new ids in front because we use pop() later
                        if (isString(tCurrentBasketId)) {
                            tBasketIdMap.set(tItem.geometryId, [tItem.itemId, tCurrentBasketId]);
                        }
                        else {
                            tCurrentBasketId.unshift(tItem.itemId);
                        }
                    }
                }
            }
        }
        //create nodes
        const tResults = new Array();
        for (const tObj of pGfjData.Objects) {
            const tLoadedBasketIds = new Set();
            const tValidMeshes = new Array();
            const tNode = this.loadObject(pGfjData, tObj, null, pRootBasketId, tBasketIdMap, tLoadedBasketIds, tValidMeshes);
            if (tNode != null && tValidMeshes.length > 0) {
                tResults.push(tNode);
                //handle basket items without geometry
                if (pBasketItems != null) {
                    for (const tItem of pBasketItems) {
                        if (!tLoadedBasketIds.has(tItem.itemId)) {
                            this.createDummySubArticle(tNode, tItem.itemId);
                        }
                    }
                }
            }
        }
        return (tResults);
    }
    async preLoadResources(pGfjData, pContainer) {
        //this has to be synchronized in case multiple gfj files are loaded at the same time
        const tLockContext = await this.mPreloadResourcesLock.acquireLock();
        try {
            const tPromises = new Array();
            //pre-load geometries
            for (const tGeomId in pGfjData.Geometries) {
                tPromises.push(this.loadGeometry(tGeomId, pGfjData.Geometries[tGeomId], pContainer));
            }
            //pre-load materials
            for (const tMatName in pGfjData.Materials) {
                tPromises.push(this.loadMaterial(tMatName, pGfjData.Materials[tMatName], pContainer));
            }
            await Promise.all(tPromises);
        }
        finally {
            tLockContext.unlock();
        }
    }
    loadObject(pGfjData, pGfjObj, pParent, pRootBasketId, pBasketIdMap, pLoadedBasketIds, pValidMeshes) {
        if (pGfjObj == null)
            return (null);
        let tName = "gfj_";
        let tMesh = null;
        let tNode;
        //create a Mesh if it is a geometry node otherwise a TransformNode
        if (pGfjObj.Geometry != null) {
            //build name
            tName += "mesh";
            if (pGfjObj.Layer != null)
                tName += "_" + pGfjObj.Layer;
            tMesh = new Mesh(tName, this.mScene);
            tNode = tMesh;
        }
        else {
            //build name
            const tOrderNum = pGfjObj.OrderNumber;
            if (pParent == null) {
                tName += "article_" + tOrderNum;
            }
            else {
                tName += tOrderNum != null ? "subarticle_" + tOrderNum : "group";
            }
            tNode = new TransformNode(tName, this.mScene);
        }
        //parent
        tNode.parent = pParent;
        //get basket id
        let tBasketId = this.getBasketId(pGfjObj, pBasketIdMap);
        //basket id is optional for root element
        if (pParent == null && pRootBasketId != null && tBasketId == null)
            tBasketId = pRootBasketId;
        //assign basket id
        if (tBasketId != null) {
            pLoadedBasketIds.add(tBasketId);
            NodeUserData.SetData(tNode, ARTICLEELEMENT_NODEDATA_BASKETID, tBasketId);
            //selectable flag for article
            const tSelectable = pGfjObj.Selectable == null || pGfjObj.Selectable === "Self";
            NodeUserData.SetData(tNode, ARTICLEELEMENT_NODEDATA_SELECTABLE, tSelectable);
        }
        //transformation
        if (pGfjObj.Transform != null) {
            if (pGfjObj.Transform.Position != null) {
                tNode.position = new Vector3(pGfjObj.Transform.Position[0], pGfjObj.Transform.Position[1], -pGfjObj.Transform.Position[2]);
            }
            if (pGfjObj.Transform.Rotation != null) {
                tNode.rotationQuaternion = new Quaternion(pGfjObj.Transform.Rotation[0], pGfjObj.Transform.Rotation[1], -pGfjObj.Transform.Rotation[2], -pGfjObj.Transform.Rotation[3]);
            }
            if (pGfjObj.Transform.Scale != null) {
                tNode.scaling = new Vector3(pGfjObj.Transform.Scale[0], pGfjObj.Transform.Scale[1], pGfjObj.Transform.Scale[2]);
            }
        }
        if (tMesh != null) {
            //geometry
            if (pGfjObj.Geometry != null) {
                const tGeom = this.mGeometryManager.getGeometry(pGfjObj.Geometry);
                if (tGeom != null) {
                    tGeom.applyToMesh(tMesh);
                    pValidMeshes.push(tMesh);
                }
            }
            //material
            let tMaterial = null;
            const tMatName = pGfjObj.Material;
            if (!isNullOrEmpty(tMatName)) {
                const tGfjMat = pGfjData.Materials[tMatName];
                if (tGfjMat != null && tGfjMat.wcf_id != null) {
                    tMaterial = this.mMaterialManager.getMaterial(tGfjMat.wcf_id);
                }
            }
            if (tMaterial == null)
                tMaterial = this.mMaterialManager.defaultMaterial;
            tMesh.material = tMaterial;
            //layer
            if (pGfjObj.Layer != null) {
                this.mLayerManager.setMeshLayer(tMesh, pGfjObj.Layer);
            }
        }
        //load children
        if (pGfjObj.Children != null) {
            for (const tChild of pGfjObj.Children) {
                this.loadObject(pGfjData, tChild, tNode, null, pBasketIdMap, pLoadedBasketIds, pValidMeshes);
            }
        }
        tNode.freezeWorldMatrix(); //freeze world matrix for better performance
        return (tNode);
    }
    createDummySubArticle(pParent, pBasketId) {
        const tNode = new TransformNode("gfj_dummy_subarticle", this.mScene);
        tNode.parent = pParent;
        tNode.freezeWorldMatrix(); //freeze world matrix for better performance
        NodeUserData.SetData(tNode, ARTICLEELEMENT_NODEDATA_BASKETID, pBasketId);
        NodeUserData.SetData(tNode, ARTICLEELEMENT_NODEDATA_SELECTABLE, true);
    }
    getBasketId(pGfjObj, pBasketIdMap) {
        let tBasketId;
        if (pGfjObj.GeometryId != null && pBasketIdMap != null) {
            const tMapId = pBasketIdMap.get(pGfjObj.GeometryId);
            if (tMapId != null) {
                if (isString(tMapId)) {
                    tBasketId = tMapId;
                }
                else {
                    //use pop to get first id and make sure that the same id will not be used again
                    tBasketId = tMapId.pop();
                }
            }
        }
        else {
            tBasketId = pGfjObj.BasketId; //fallback
        }
        return (tBasketId);
    }
    async loadGeometry(pId, pUrl, pContainer) {
        if (isNullOrEmpty(pId))
            return;
        const tGeom = this.mGeometryManager.getGeometry(pId);
        if (tGeom != null && !this.mGeometryManager.isFallbackGeometry(tGeom))
            return;
        const tLockContext = await this.mResourceLoadingLock.acquireLock();
        try {
            const tUrl = this.resolveUrl(pUrl);
            let tMeshes = null;
            if (pContainer != null) {
                const tBuffer = await pContainer.getFile(tUrl, true);
                tMeshes = GltfMesh.Import(tBuffer, this.mScene);
            }
            else {
                tMeshes = await GltfMesh.ImportFromUrl(tUrl, this.mScene);
            }
            let tMesh = null;
            if (tMeshes != null && tMeshes.length > 0) {
                //only take the first mesh, gfj should have only one mesh per gltf
                tMesh = tMeshes[0];
            }
            if (tMesh != null) {
                //ignore geometries with invalid bounds
                if (BoundingBox.IsBoundingInfoValid(tMesh.getBoundingInfo())) {
                    this.mGeometryManager.addGeometryMesh(pId, tMesh);
                }
                else {
                    Log.warn("Detected geometry with invalid bounding box: " + pId);
                    tMesh.dispose();
                }
            }
        }
        catch (pError) {
            Log.error("Failed to load geometry " + pId, pError);
        }
        finally {
            tLockContext.unlock();
        }
    }
    async loadMaterial(pName, pMatData, pContainer) {
        const tId = await this.getMaterialId(pName, pMatData);
        if (isNullOrEmpty(tId))
            return;
        //need to update exsisting material?
        const tCurrentMat = this.mMaterialManager.getMaterial(tId);
        if (tCurrentMat != null) {
            if (tCurrentMat.hasFallbackTextures()) {
                //load missing textures
                await this.loadPbrMaterialTextures(pMatData, tCurrentMat, pContainer);
            }
            return;
        }
        //load new material
        if (this.isPbrMaterial(pMatData)) {
            await this.loadPbrMaterial(tId, pName, pMatData, pContainer);
        }
        else {
            await this.loadLegacyMaterialAsPbr(tId, pName, pMatData, pContainer);
        }
    }
    /*
    Note: This can't be removed because GfjLoader is also used from PEC import.
    Old PEC files with legacy materials may still be in use somewhere.
    */
    async loadLegacyMaterialAsPbr(pId, pName, pMatData, pContainer) {
        const tMat = new PbrMaterial(pName, this.mMaterialManager);
        //load textures
        await this.loadPbrMaterialTextures(pMatData, tMat, pContainer);
        //diffuse color
        let tDiffuseColor = Color3.White();
        if (tMat.albedoTexture == null) {
            if (pMatData.dif != null) {
                tDiffuseColor = new Color3(clamp(pMatData.dif[0], 0, 1), clamp(pMatData.dif[1], 0, 1), clamp(pMatData.dif[2], 0, 1));
            }
        }
        //transparency
        if (pMatData.tra != null && (tMat.albedoTexture == null || !tMat.albedoTexture.hasAlpha)) {
            tMat.alpha = 1.0 - pMatData.tra;
        }
        //emission color
        if (pMatData.emission != null) {
            tMat.emissiveColor = new Color3(clamp(pMatData.emission[0], 0, 1), clamp(pMatData.emission[1], 0, 1), clamp(pMatData.emission[2], 0, 1));
        }
        //specular color
        let tSpecularColor = Color3.Black();
        if (pMatData.spe != null) {
            tSpecularColor = new Color3(clamp(pMatData.spe[0], 0, 1), clamp(pMatData.spe[1], 0, 1), clamp(pMatData.spe[2], 0, 1));
        }
        //shininess
        let tShininess = 30;
        if (pMatData.shi != null)
            tShininess = pMatData.shi;
        //reflectivitiy
        let tRefl = 0.0;
        if (pMatData.reflection != null) {
            tRefl = (tSpecularColor.r + tSpecularColor.g + tSpecularColor.b) / 3.0;
            tRefl *= pMatData.reflection;
            if (tRefl > 1.0)
                tRefl = 1.0;
        }
        //get PBR parmaters
        const tParams = PbrMaterial.ConvertToPbrParameters(tDiffuseColor, tMat.albedoTexture != null, tSpecularColor, tShininess, tRefl, tMat.alpha, pMatData.type);
        tMat.albedoColor = tParams.albedo;
        tMat.metallic = tParams.metallic;
        tMat.roughness = tParams.roughness;
        tMat.freeze(); //better performance, materials do not change
        this.mMaterialManager.addMaterial(pId, tMat);
    }
    async loadPbrMaterial(pId, pName, pMatData, pContainer) {
        const tMat = new PbrMaterial(pName, this.mMaterialManager);
        //load textures
        await this.loadPbrMaterialTextures(pMatData, tMat, pContainer);
        //albedo color
        if (tMat.albedoTexture == null) {
            const tColor = pMatData.baseColor != null ? pMatData.baseColor : pMatData.dif;
            if (tColor != null) {
                tMat.albedoColor = new Color3(clamp(tColor[0], 0, 1), clamp(tColor[1], 0, 1), clamp(tColor[2], 0, 1));
                tMat.albedoColor.toLinearSpaceToRef(tMat.albedoColor);
            }
        }
        //transparency
        if (pMatData.tra != null && (tMat.albedoTexture == null || !tMat.albedoTexture.hasAlpha)) {
            tMat.alpha = 1.0 - pMatData.tra;
        }
        //emission color
        if (pMatData.emission != null) {
            tMat.emissiveColor = new Color3(clamp(pMatData.emission[0], 0, 1), clamp(pMatData.emission[1], 0, 1), clamp(pMatData.emission[2], 0, 1));
        }
        //metallic
        if (tMat.metallicTexture == null) {
            let tMetallic = pMatData.metallic != null ? pMatData.metallic : 0.0;
            if (tMetallic > 1.0)
                tMetallic = 1.0;
            tMat.metallic = tMetallic;
        }
        //roughness
        if (tMat.microSurfaceTexture == null) {
            const tRoughness = pMatData.roughness != null ? pMatData.roughness : 0.0;
            tMat.roughness = clamp(tRoughness, 0.0, 1.0);
        }
        tMat.freeze(); //better performance, materials do not change
        this.mMaterialManager.addMaterial(pId, tMat);
    }
    setTexture(pMat, pTexture, pTexType) {
        if (pMat == null || pTexture == null)
            return;
        const tOldTexture = pMat[pTexType];
        pMat[pTexType] = pTexture;
        //dispose old texture if it is a clone
        if (tOldTexture != null && tOldTexture instanceof Texture && tOldTexture.isClone) {
            tOldTexture.dispose();
        }
        pMat.markDirty();
    }
    async loadPbrMaterialTextures(pMatData, pMat, pContainer) {
        await Promise.all([
            this.loadAlbedoTexture(pMatData, pMat, pContainer),
            this.loadBumpTexture(pMatData, pMat, pContainer),
            this.loadMetallicTexture(pMatData, pMat, pContainer),
            this.loadRoughnessTexture(pMatData, pMat, pContainer)
        ]);
    }
    async loadAlbedoTexture(pMatData, pMat, pContainer) {
        const tTexUrl = this.resolveUrl(pMatData.tex);
        if (isNotNullOrEmpty(tTexUrl) && pMatData.texChecksum != null) {
            try {
                const tHasAlpha = pMatData.texTransparent != null && pMatData.texTransparent === true;
                const tTexture = await this.loadTexture(pMat, tTexUrl, pMatData.texChecksum, "albedoTexture", tHasAlpha, pContainer, pMatData.scale, pMatData.rotate, pMatData.offset);
                if (tTexture != null) {
                    pMat.useAlphaFromAlbedoTexture = tTexture.hasAlpha;
                    if (tTexture.hasAlpha && pMatData.semiTransparent === false) {
                        pMat.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHATEST;
                    }
                }
            }
            catch (pError) {
                Log.error("Failed to load albedo texture " + tTexUrl, pError);
            }
        }
    }
    async loadBumpTexture(pMatData, pMat, pContainer) {
        const tBumpsUrl = this.resolveUrl(pMatData.bumps);
        if (isNotNullOrEmpty(tBumpsUrl) && pMatData.bumpsChecksum != null) {
            try {
                const tTexture = await this.loadTexture(pMat, tBumpsUrl, pMatData.bumpsChecksum, "bumpTexture", false, pContainer, pMatData.nscale != null ? pMatData.nscale : pMatData.scale, pMatData.nrotate != null ? pMatData.nrotate : pMatData.rotate, pMatData.noffset != null ? pMatData.noffset : pMatData.offset);
                if (tTexture != null) {
                    pMat.forceIrradianceInFragment = true;
                }
            }
            catch (pError) {
                Log.error("Failed to load bump texture " + tBumpsUrl, pError);
            }
        }
    }
    async loadMetallicTexture(pMatData, pMat, pContainer) {
        const tMetallicUrl = this.resolveUrl(pMatData.metallicTex);
        if (isNotNullOrEmpty(tMetallicUrl) && pMatData.metallicChecksum != null) {
            try {
                const tTexture = await this.loadTexture(pMat, tMetallicUrl, pMatData.metallicChecksum, "metallicTexture", false, pContainer, pMatData.scale, pMatData.rotate, pMatData.offset);
                if (tTexture != null) {
                    pMat.metallic = 1.0;
                }
            }
            catch (pError) {
                Log.error("Failed to load metalllic texture " + tMetallicUrl, pError);
            }
        }
    }
    async loadRoughnessTexture(pMatData, pMat, pContainer) {
        const tRoughnessUrl = this.resolveUrl(pMatData.roughnessTex);
        if (isNotNullOrEmpty(tRoughnessUrl) && pMatData.roughnessChecksum != null) {
            try {
                const tTexture = await this.loadTexture(pMat, tRoughnessUrl, pMatData.roughnessChecksum, "microSurfaceTexture", false, pContainer, pMatData.scale, pMatData.rotate, pMatData.offset);
                if (tTexture != null) {
                    pMat.roughness = 1.0;
                }
            }
            catch (pError) {
                Log.error("Failed to load roughness texture " + tRoughnessUrl, pError);
            }
        }
    }
    async loadTexture(pMat, pUrl, pCheckSum, pTexType, pHasAlpha, pContainer, pScaleData, pRotateData, pOffsetData) {
        //check if this texture is currently loading and wait until its loaded
        let tLoadLock = this.mLoadingTexturesMap.get(pCheckSum);
        if (tLoadLock != null) {
            await tLoadLock.waitWhileLocked();
        }
        //lock this texture
        tLoadLock = new Lock();
        const tLoadLockContext = await tLoadLock.acquireLock();
        this.mLoadingTexturesMap.set(pCheckSum, tLoadLock);
        //lock to limit max loading recources
        const tLockContext = await this.mResourceLoadingLock.acquireLock();
        try {
            let tTexture = await this.mMaterialManager.createResourceTexture(pCheckSum, pUrl, pHasAlpha, pContainer);
            if (tTexture != null) {
                tTexture.name = getFileName(pUrl);
                tTexture = this.setupTextureMatrix(tTexture, pScaleData, pRotateData, pOffsetData);
                this.setTexture(pMat, tTexture, pTexType);
            }
            return (tTexture);
        }
        finally {
            this.mLoadingTexturesMap.delete(pCheckSum); //remove texture loading lock
            tLoadLockContext.unlock();
            tLockContext.unlock();
        }
    }
    setupTextureMatrix(pTexture, pScaleData, pRotateData, pOffsetData) {
        let tTexture = pTexture;
        if (pOffsetData != null || pScaleData != null || pRotateData != null) {
            //use clone to allow different texture matrix
            tTexture = tTexture.clone();
            if (pScaleData != null) {
                tTexture.uScale = pScaleData[0];
                tTexture.vScale = pScaleData[1];
            }
            if (pRotateData != null) {
                tTexture.uAng = degToRad(pRotateData[0]);
                tTexture.vAng = degToRad(pRotateData[1]);
                tTexture.wAng = degToRad(pRotateData[2]);
            }
            if (pOffsetData != null) {
                tTexture.uOffset = pOffsetData[0];
                tTexture.vOffset = pOffsetData[1];
            }
            tTexture.notifyTextureMatrixChanged();
        }
        return (tTexture);
    }
    async getMaterialId(pName, pMatData) {
        if (pMatData == null || isNullOrEmpty(pMatData.checksum))
            return (null);
        if (pMatData.wcf_id != null)
            return (pMatData.wcf_id);
        //combine name and checksum, checksum alone is not enough because the name
        //may contain modifiers for the material
        const tHash = await hash(pName + pMatData.checksum, "SHA-256");
        const tId = tHash.substr(0, 32); // reduce to 128 bit, should be enough in this case
        //cache the id
        pMatData.wcf_id = tId;
        return (tId);
    }
    resolveUrl(pUrl) {
        if (pUrl == null)
            return ("");
        if (this.onResolveUrl == null)
            return (pUrl);
        else
            return (this.onResolveUrl(pUrl));
    }
    isPbrMaterial(pMatData) {
        if (pMatData.metallic != null || pMatData.metallicTex != null ||
            pMatData.roughness != null || pMatData.roughnessTex != null) {
            return (true);
        }
        return (false);
    }
}
